import React from 'react';
import classNames from 'classnames/bind';
import { filesize } from 'filesize';
import { useParams } from 'react-router-dom';

import { OrganizationParams } from 'types/route_params';
import { PricingPlan } from 'types/pricing_plan';

import { useCurrentPlan } from 'organizations/hooks/useCurrentPlan';
import { useHasAccess } from 'hooks/useHasAccess';
import { useOrganization } from 'hooks/useOrganization';
import { usePricingPlans } from 'organizations/hooks/usePricingPlans';
import { useShowFrontChatWithIdentity } from 'hooks/useShowFrontChatWithIdentity';

import { Button } from 'components/Button/Button';
import { ExternalLink } from 'components/ExternalLink/ExternalLink';
import { FormMessage } from 'components/FormMessage/FormMessage';
import { Heading } from 'components/Heading/Heading';
import * as List from 'components/List';
import { Loader } from 'components/Loader/Loader';
import * as PageLayout from 'components/PageLayout';
import { Text } from 'components/Text/Text';

import { UpgradePlanDialog } from 'organizations/components/UpgradePlanDialog/UpgradePlanDialog';

import { ComparePlansListItem } from './ListItem';
import styles from './ComparePlansView.module.scss';

const c = classNames.bind(styles);

const numberFormat = new Intl.NumberFormat('en-us');

function getFilesizeLabel(plan: PricingPlan | null, value: number | null) {
  if (!value)
    return plan?.price_description === 'Custom' ? 'Custom' : 'Unlimited';

  return filesize(value, { base: 2 });
}

function getLabel(
  plan: PricingPlan | null,
  value: number | null,
  label: string
) {
  if (!value)
    return `${plan?.price_description === 'Custom' ? 'Custom' : 'Unlimited'} ${label}s`;

  const labelStr = `${label}${value > 1 ? 's' : ''}`;

  return `${numberFormat.format(value)} ${labelStr}`;
}

export function ComparePlansView() {
  const { organizationID } = useParams<OrganizationParams>();
  const { data: organization } = useOrganization(organizationID);
  const { data: plans, isLoading: isLoadingPlans } = usePricingPlans();

  const { data: currentPlan, isLoading: isLoadingCurrentPlan } =
    useCurrentPlan();

  const showFrontChatWithIdentity = useShowFrontChatWithIdentity();

  const [hasAccess] = useHasAccess();

  const isLoading = isLoadingCurrentPlan || isLoadingPlans;

  const [selectedPlanIdForUpgrade, setSelectedPlanIdForUpgrade] =
    React.useState<string | null>(null);

  if (!organization) {
    return null;
  }

  // Sort plans so the current plan is first, then plans without extensions,
  // then plans with extensions in display order
  const sortedPlans = plans
    ?.sort((a, b) => {
      // Current plan should come first
      if (a.id === currentPlan?.id) return -1;
      if (b.id === currentPlan?.id) return 1;

      // Plans without extensions come last
      if (!a.extension && !b.extension) return 0;
      if (!a.extension) return 1;
      if (!b.extension) return -1;

      // Sort remaining plans by display_order
      return a.extension.display_order - b.extension.display_order;
    })
    .filter((plan) => {
      // Always show current plan.
      if (currentPlan?.id === plan.id) {
        return true;
      }

      // If the user is on a self-serve plan, show all plans.
      if (currentPlan?.extension?.self_serve) {
        return true;
      }

      // If the user is on a non-self-serve plan, hide all other plans.
      return false;
    })
    .map((plan) => (plan.id === currentPlan?.id ? currentPlan : plan));

  return (
    <PageLayout.Root>
      <div>
        <PageLayout.Header>
          <div className={c('head')}>
            <div>
              <Text>{organization.name}</Text>
              <Heading level="1">Compare plans</Heading>
              <ExternalLink href="https://docs.lumeo.com/docs/plans">
                View all plans
              </ExternalLink>
            </div>
            <div>
              <Text>
                Any questions?{' '}
                <Button variant="link" onClick={showFrontChatWithIdentity}>
                  Chat with us
                </Button>
              </Text>
            </div>
          </div>
        </PageLayout.Header>
        {!hasAccess('billing') && (
          <PageLayout.Header>
            <FormMessage icon="info" intent="warning">
              Please contact a billing user to change plans.
            </FormMessage>
          </PageLayout.Header>
        )}
      </div>
      <PageLayout.Content className={c('content')}>
        {isLoading ? (
          <Loader size="small" text="Loading pricing plans..." />
        ) : (
          <>
            <PageLayout.Container>
              <div className={c('grid', 'header')}>
                {sortedPlans &&
                  sortedPlans.map((plan) => (
                    <div
                      className={c('header-item', 'grid-item', {
                        'current-plan': currentPlan?.id === plan.id,
                      })}
                    >
                      <Heading level="2">{plan.name}</Heading>
                      <Text>{plan.description}</Text>
                      <div className={c('header-item-pricing')}>
                        <Text className={c('header-item-pricing-value')}>
                          <em>{plan.price_description}</em>
                        </Text>

                        {currentPlan?.id === plan.id ? (
                          <Button variant="primary" disabled>
                            Current plan
                          </Button>
                        ) : (
                          hasAccess('billing') &&
                          (plan.extension?.self_serve &&
                          currentPlan?.name === 'Starter' ? (
                            <UpgradePlanDialog
                              trigger={
                                <Button
                                  variant="primary"
                                  onClick={() =>
                                    setSelectedPlanIdForUpgrade(plan.id)
                                  }
                                >
                                  Switch to this plan
                                </Button>
                              }
                              open={selectedPlanIdForUpgrade === plan.id}
                              onOpenChange={(open) => {
                                if (!open) setSelectedPlanIdForUpgrade(null);
                              }}
                              onSuccess={() => {
                                setSelectedPlanIdForUpgrade(null);
                              }}
                              planID={plan.id}
                            />
                          ) : (
                            <Button
                              variant="secondary"
                              onClick={showFrontChatWithIdentity}
                            >
                              Contact Sales
                            </Button>
                          ))
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            </PageLayout.Container>
            <PageLayout.Container>
              <div className={c('grid')}>
                {sortedPlans &&
                  sortedPlans.map((plan) => (
                    <div
                      className={c('grid-item', {
                        'current-plan': currentPlan?.id === plan.id,
                      })}
                    >
                      <Heading className={c('grid-heading')} level="4" asChild>
                        <h3>Features:</h3>
                      </Heading>
                      <List.Root>
                        {(plan.extension?.features ?? []).length === 0 ? (
                          <ComparePlansListItem>Custom</ComparePlansListItem>
                        ) : (
                          (plan.extension?.features?.map((feature) => (
                            <ComparePlansListItem key={feature}>
                              {feature}
                            </ComparePlansListItem>
                          )) ?? [])
                        )}
                      </List.Root>
                    </div>
                  ))}
              </div>
            </PageLayout.Container>
            <PageLayout.Container>
              <div className={c('grid')}>
                {sortedPlans &&
                  sortedPlans.map((plan) => (
                    <div
                      className={c('grid-item', {
                        'current-plan': currentPlan?.id === plan.id,
                      })}
                    >
                      <Heading className={c('grid-heading')} level="4" asChild>
                        <h3>Usage includes:</h3>
                      </Heading>
                      <List.Root>
                        <ComparePlansListItem>
                          {getLabel(plan, plan.application_limit, 'workspace')}
                        </ComparePlansListItem>
                        <ComparePlansListItem>
                          {getLabel(plan, plan.gateway_limit, 'gateway')}
                        </ComparePlansListItem>
                        <ComparePlansListItem>
                          {getLabel(
                            plan,
                            plan.running_deployment_limit,
                            'running deployment'
                          )}
                        </ComparePlansListItem>
                        <ComparePlansListItem>
                          {getLabel(
                            plan,
                            plan.included_monthly_active_live_streams,
                            'monthly-active live stream'
                          )}
                          <br />
                          {plan.extension?.monthly_active_live_streams?.map(
                            (addon) => (
                              <Text size="small" inline>
                                {addon}
                              </Text>
                            )
                          )}
                        </ComparePlansListItem>
                        <ComparePlansListItem>
                          {getLabel(
                            plan,
                            plan.included_file_processing_minutes,
                            'minute'
                          )}{' '}
                          of recorded analytics
                          <br />
                          {plan.extension?.file_processing_minutes?.map(
                            (addon) => (
                              <Text size="small" inline>
                                {addon}
                              </Text>
                            )
                          )}
                        </ComparePlansListItem>
                        {plan.can_retain_recordings ? (
                          <ComparePlansListItem>
                            {getLabel(
                              plan,
                              plan.recording_retention_days,
                              'day'
                            )}{' '}
                            of cloud storage retention per live stream
                            <br />
                            {plan.extension?.recording_retention?.map(
                              (addon) => (
                                <Text size="small" inline>
                                  {addon}
                                </Text>
                              )
                            )}
                          </ComparePlansListItem>
                        ) : (
                          <ComparePlansListItem>
                            {getFilesizeLabel(
                              plan,
                              plan.included_cloud_storage_bytes
                            )}{' '}
                            cloud storage
                            <br />
                            {plan.extension?.cloud_storage?.map((addon) => (
                              <>
                                <Text size="small" inline>
                                  {addon}
                                </Text>
                                <br />
                              </>
                            ))}
                          </ComparePlansListItem>
                        )}
                        {(plan.extension?.lumeo_cloud_gateway?.length ?? 0) >
                          0 && (
                          <ComparePlansListItem>
                            Lumeo Cloud Gateway
                            <br />
                            {plan.extension?.lumeo_cloud_gateway?.map(
                              (addon) => (
                                <>
                                  <Text type="paragraph" size="small" inline>
                                    {addon}
                                  </Text>
                                  <br />
                                </>
                              )
                            )}
                          </ComparePlansListItem>
                        )}
                      </List.Root>
                    </div>
                  ))}
              </div>
            </PageLayout.Container>
            <PageLayout.Container>
              <div className={c('grid')}>
                {sortedPlans &&
                  sortedPlans.map((plan) => (
                    <div
                      className={c('grid-item', {
                        'current-plan': currentPlan?.id === plan.id,
                      })}
                    >
                      <Heading className={c('grid-heading')} level="4" asChild>
                        <h3>Support:</h3>
                      </Heading>
                      <List.Root>
                        {(plan.extension?.support ?? []).map((feature) => (
                          <ComparePlansListItem>{feature}</ComparePlansListItem>
                        ))}
                      </List.Root>
                    </div>
                  ))}
              </div>
            </PageLayout.Container>
          </>
        )}
      </PageLayout.Content>
    </PageLayout.Root>
  );
}
